import { BarcodeScanInput } from '@/components/barcode';
import { ProductSelect, useProductCatalog } from '@/components/products';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { deriveTotalUnits } from '@/lib/formatCaseUnits';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
}

interface Supplier {
  id: string;
  code: string;
  name: string;
}

interface ReceiptLine {
  productId: string;
  casesReceived: string;
  leftoverUnits: string;
  batchNumber: string;
  expiryDate: string;
}

export default function GoodsReceiptFormPage() {
  const navigate = useNavigate();
  const { products, loading: productsLoading, error: productsError } = useProductCatalog({
    activeForOps: true,
  });
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [receiptDate, setReceiptDate] = useState(() =>
    new Date().toISOString().split('T')[0]
  );
  const [invoiceReference, setInvoiceReference] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReceiptLine[]>([
    { productId: '', casesReceived: '', leftoverUnits: '', batchNumber: '', expiryDate: '' },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [whRes, supRes] = await Promise.all([
          apiFetch('/api/v1/warehouses?type=HUB'),
          apiFetch('/api/v1/suppliers?pageSize=500'),
        ]);
        if (whRes.ok) {
          const data = await whRes.json();
          setWarehouses(data.items ?? data ?? []);
        }
        if (supRes.ok) {
          const data = await supRes.json();
          setSuppliers(data.items ?? data ?? []);
        }
      } catch {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (productsError) {
      setError(productsError);
    }
  }, [productsError]);

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { productId: '', casesReceived: '', leftoverUnits: '', batchNumber: '', expiryDate: '' },
    ]);
  };

  const removeLine = (idx: number) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  const updateLine = (idx: number, field: keyof ReceiptLine, value: string) => {
    setLines((prev) =>
      prev.map((line, i) => (i === idx ? { ...line, [field]: value } : line))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const linesWithQuantity = lines.filter((l) => {
      if (!l.productId) return false;
      const cases = parseInt(l.casesReceived, 10) || 0;
      const leftover = parseInt(l.leftoverUnits, 10) || 0;
      return cases > 0 || leftover > 0;
    });

    if (!supplierId || !warehouseId || !receiptDate || linesWithQuantity.length === 0) {
      setError('Please fill in supplier, warehouse, date, and at least one product line.');
      return;
    }

    for (let i = 0; i < linesWithQuantity.length; i += 1) {
      const line = linesWithQuantity[i];
      if (!line.batchNumber.trim()) {
        setError(`Batch number is required on line ${i + 1}.`);
        return;
      }
      if (!line.expiryDate) {
        setError(`Expiry date is required on line ${i + 1}.`);
        return;
      }
    }

    const validLines = linesWithQuantity.map((l) => {
        const product = products.find((p) => p.id === l.productId);
        const upc = product?.unitsPerCase ?? 1;
        const casesReceived = parseInt(l.casesReceived, 10) || 0;
        const leftoverUnits = parseInt(l.leftoverUnits, 10) || 0;
        return {
          productId: l.productId,
          casesReceived,
          leftoverUnits,
          quantity: deriveTotalUnits(casesReceived, leftoverUnits, upc),
          batchNumber: l.batchNumber.trim(),
          expiryDate: new Date(l.expiryDate).toISOString(),
        };
      });

    setSaving(true);
    try {
      const res = await apiFetch('/api/v1/goods-receipts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplierId,
          warehouseId,
          receiptDate: new Date(receiptDate).toISOString(),
          invoiceReference: invoiceReference.trim() || null,
          lines: validLines,
          notes: notes.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? 'Failed to create goods receipt');
      }
      const receipt = await res.json();
      navigate(`/inventory/goods-receipts/${receipt.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create goods receipt');
    } finally {
      setSaving(false);
    }
  };

  if (loading || productsLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <Button variant="ghost" className="mb-4" onClick={() => navigate('/goods-receipts')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Goods Receipts
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>New Goods Receipt</CardTitle>
            <CardDescription>
              Record incoming stock from a supplier. Goods receipts can only be created for Hub warehouses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  {error}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="supplier">Supplier *</Label>
                  <Select value={supplierId} onValueChange={setSupplierId} required>
                    <SelectTrigger id="supplier">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.code} – {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="warehouse">Destination Warehouse (Hub) *</Label>
                  <Select value={warehouseId} onValueChange={setWarehouseId} required>
                    <SelectTrigger id="warehouse">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {warehouseOptionLabel(w)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="receiptDate">Receipt Date *</Label>
                  <Input
                    id="receiptDate"
                    type="date"
                    value={receiptDate}
                    onChange={(e) => setReceiptDate(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="invoiceReference">Invoice reference</Label>
                  <Input
                    id="invoiceReference"
                    value={invoiceReference}
                    onChange={(e) => setInvoiceReference(e.target.value)}
                    placeholder="INV-2026-0312"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Product Lines *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLine}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add line
                  </Button>
                </div>
                <div className="space-y-3">
                  {lines.map((line, idx) => (
                    <div
                      key={idx}
                      className="flex flex-wrap gap-3 items-end p-3 rounded-md border bg-muted/30"
                    >
                      <div className="flex-1 min-w-[180px]">
                        <Label className="text-xs">Product</Label>
                        <ProductSelect
                          value={line.productId}
                          onValueChange={(v) => updateLine(idx, 'productId', v)}
                          products={products}
                        />
                      </div>
                      <div className="w-20">
                        <Label className="text-xs">Cases</Label>
                        <Input
                          type="number"
                          min={0}
                          value={line.casesReceived}
                          onChange={(e) => updateLine(idx, 'casesReceived', e.target.value)}
                        />
                      </div>
                      <div className="w-20">
                        <Label className="text-xs">Leftover units</Label>
                        <Input
                          type="number"
                          min={0}
                          value={line.leftoverUnits}
                          onChange={(e) => updateLine(idx, 'leftoverUnits', e.target.value)}
                        />
                      </div>
                      <div className="w-16 text-xs text-muted-foreground pb-2">
                        {line.productId && (() => {
                          const p = products.find((x) => x.id === line.productId);
                          const upc = p?.unitsPerCase ?? 1;
                          const c = parseInt(line.casesReceived, 10) || 0;
                          const u = parseInt(line.leftoverUnits, 10) || 0;
                          if (c === 0 && u === 0) return null;
                          return `= ${deriveTotalUnits(c, u, upc)}`;
                        })()}
                      </div>
                      <div className="w-40 min-w-[10rem]">
                        <Label className="text-xs">Batch *</Label>
                        <BarcodeScanInput
                          value={line.batchNumber}
                          onChange={(value) => updateLine(idx, 'batchNumber', value)}
                          placeholder="Batch #"
                          scanTitle="Scan batch barcode"
                          scanDescription="Scan the supplier batch or lot barcode."
                          required
                        />
                      </div>
                      <div className="w-36">
                        <Label className="text-xs">Expiry *</Label>
                        <Input
                          type="date"
                          value={line.expiryDate}
                          onChange={(e) => updateLine(idx, 'expiryDate', e.target.value)}
                          required
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional details..."
                  rows={2}
                  maxLength={500}
                  className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/goods-receipts')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Creating...' : 'Create goods receipt'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
